import Sortable from "sortablejs";

/**
 * Phoenix LiveView Hook for Drag and Drop functionality using SortableJS
 *
 * This hook provides sortable/draggable functionality for HTML elements in Phoenix LiveView.
 * It supports both simple list sorting and complex nested group sorting scenarios.
 *
 * @example Simple table row sorting:
 * Hook is automatically added to the table component when `drag_n_drop` is set to true.
 * In this case, every row in the table is draggable.
 * ```html
 * <.table
 *   id="document-type-table"
 *   drag_n_drop={true}
 *   on_sort_update_event="update_records_position"
 * >
 *   <:col :let={item}>
 *     <div class="flex items-center gap-x-4" data-sort-id={item.id}>
 *       <Heroicons.Outline.sort_handler />
 *       <div>{item.name}</div>
 *     </div>
 *   </:col>
 * </.table>
 * ```
 *
 * @example Nested group sorting:
 * Hook & `data-sort-event` should be manually added to the table element with nested sortable groups.
 * In this case, each row inside element with `data-sort-group` attribute will be draggable within its group.
 * ```html
 * <.table
 *   id="seafarer-ranks-table"
 *   phx-hook="HandleDragNDrop"
 *   data-sort-event="update_records_position"
 * >
 *   <tbody data-sort-group="departments">
 *     <!-- Department groups -->
 *     <tr>
 *       <td>
 *         <.table>
 *           <tbody data-sort-group="ranks">
 *             <tr data-sort-id={rank.id}>
 *               <td>
 *                 <div class="flex items-center gap-x-4">
 *                   <Heroicons.Outline.sort_handler />
 *                   {rank.name}
 *                 </div>
 *               </td>
 *             </tr>
 *           </tbody>
 *         </.table>
 *       </td>
 *     </tr>
 *   </tbody>
 * </table>
 * ```
 *
 * Required HTML attributes:
 * - `data-sort-id`: Unique identifier for each sortable item (required on draggable elements).
 * These are the IDs that will be sent to the server in the new order.
 * - `data-sort-event`: Phoenix event to trigger when sort order changes (optional, set via component)
 *
 * Optional HTML attributes:
 * - `data-sort-group`: Groups elements for nested sorting scenarios
 *
 * Phoenix LiveView Integration:
 * The hook automatically pushes a Phoenix event when items are reordered, sending:
 * ```elixir
 * %{"data" => [ordered_list_of_ids]}
 * ```
 *
 * Handle the event in your LiveView:
 * ```elixir
 * def handle_event("update_records_position", %{"data" => sorted_ids}, socket) do
 *   # Update your data structure with new order
 *   case MyContext.sort_items(sorted_ids) do
 *     {:ok, _} ->
 *       socket
 *       |> push_alert("success", "Order updated")
 *       |> noreply_socket()
 *
 *     {:error, _} ->
 *       socket
 *       |> push_alert("error", "Could not update order")
 *       |> noreply_socket()
 *   end
 * end
 * ```
 */

const ORDER_ID_ATTRIBUTE = "data-sort-id";

const HandleDragNDrop = {
  /**
   * Mounted lifecycle hook - initializes SortableJS on the element
   *
   * This method:
   * 1. Identifies the sort event name from data attributes
   * 2. Finds sortable containers (either grouped or single container)
   * 3. Creates Sortable instances for each container
   * 4. Configures event handlers for sort updates
   */
  mounted(this: any) {
    const sortEvent = this.el.dataset.sortEvent;
    let sortableElementsContainer: HTMLElement[];

    // Support for grouped sorting (nested scenarios)
    // If data-sort-group elements exist, use them as containers
    // Otherwise, use the root element as the container
    const groups = this.el.querySelectorAll("[data-sort-group]");
    groups.length > 0
      ? (sortableElementsContainer = groups)
      : (sortableElementsContainer = [this.el]);

    // Initialize Sortable for each container
    sortableElementsContainer.forEach((container) => {
      const group = container.dataset.sortGroup || "default";
      Sortable.create(container, {
        group: group, // Allows items to be moved between groups
        handle: ".sort-menu", // Only allow dragging via specific handle element
        animation: 150, // Animation duration for sorting
        onEnd: (event) => {
          // Check if position actually changed
          const positionChanged =
            event.oldIndex !== event.newIndex || event.from !== event.to;

          // Collect all sortable elements and their IDs in current order
          let sortableElements: NodeListOf<HTMLElement> =
            this.el.querySelectorAll(`[${ORDER_ID_ATTRIBUTE}]`);

          let orderIds = Array.from(sortableElements).map((element) => {
            return element.getAttribute(ORDER_ID_ATTRIBUTE);
          });

          const itemId = event.item.getAttribute(ORDER_ID_ATTRIBUTE);

          // Push event to Phoenix LiveView with new order
          if (sortEvent && positionChanged) {
            this.pushEvent(sortEvent, {
              from: event.from.dataset,
              to: event.to.dataset,
              item_id: itemId,
              data: orderIds,
            });
          }
        },
      });
    });
  },
};

export default HandleDragNDrop;
